/**
 * FundingRounds.js — the deal read between its tranches
 * =====================================================
 * Brian, 28 September 2026, on Valuno Group: "we wanted to see how much money
 * we generated in the deal from selling stock before we funded tranche 2. Its
 * hard to tell that on the current report."
 *
 * The curve holds the answer and does not say it: forty small steps up and two
 * cliffs down, in two currencies, with the sale made the day tranche 2 was
 * wired netted into the cliff. The API splits the same series at each
 * tranche's wire (`funding_rounds`, reports/services/deal_cash_series.py) and
 * this card reads it out. Every figure on it is the server's; nothing is added
 * up here, so the card and the curve cannot disagree.
 *
 * It is the Tranches tab of a deal's page (usability review item 23). A deal
 * funded once has nothing to split, and the tab says so in a sentence rather
 * than drawing a card that restates the curve.
 */
import React from "react";
import { Badge, Card } from "../../components/ui";
import { InfoTip } from "../../components/PageTools";
import theme from "../../components/theme";
import { day, dealMoney } from "./figures";
import { readerText } from "./text";

export const FUNDING_ROUNDS_MIN_TRANCHES = 2;

export function showFundingRounds(deal) {
  const rounds = deal?.funding_rounds;
  return Boolean(rounds && rounds.tranches_recorded >= FUNDING_ROUNDS_MIN_TRANCHES);
}

// The currencies a figure is actually in, largest first. A currency whose
// amount is nil is not one the figure is in.
export function amountsOf(figure) {
  return Object.entries(figure?.by_currency || {})
    .map(([currency, amount]) => [currency, Number(amount)])
    .filter(([, amount]) => amount !== 0)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
}

// A figure in the currencies it was written in — "SEK 2,558,971" — or a dash
// when nothing moved. Never a converted figure wearing a native label. Two
// currencies are listed side by side and never joined with a plus: they are
// not added.
export function nativeText(figure) {
  const parts = amountsOf(figure);
  if (!parts.length) return "—";
  return parts.map(([currency, amount]) => dealMoney(amount, currency)).join(" · ");
}

// What a figure leads with and what goes under it. One currency leads with
// itself and puts its dollars beneath; two lead with the dollar total, which
// is the only single number they have, and list the parts beneath — or, with
// no fixing, list the parts and say there is no total.
export function figureLines(figure) {
  const parts = amountsOf(figure);
  if (parts.length > 1) {
    if (figure.usd === null || figure.usd === undefined) {
      return { main: nativeText(figure), sub: "no single total — an exchange rate is missing", sign: 0 };
    }
    return { main: dealMoney(figure.usd, "USD"), sub: nativeText(figure), sign: Math.sign(Number(figure.usd)) };
  }
  return {
    main: nativeText(figure),
    sub: usdText(figure),
    sign: parts.length ? Math.sign(parts[0][1]) : 0,
  };
}

// The dollars beside a figure that is not already dollars. Null when there is
// nothing to add; "n/a" rather than 0 when the fixing is missing (CLAUDE.md
// 0.30), because a zero would say the krona were worth nothing.
export function usdText(figure) {
  const parts = amountsOf(figure);
  if (!parts.length) return null;
  if (parts.length === 1 && parts[0][0] === "USD") return null;
  if (figure.usd === null || figure.usd === undefined) return "USD n/a — exchange rate missing";
  return dealMoney(figure.usd, "USD");
}

// "Tranche 2" reads "tranche 2" inside a sentence.
export const lowerFirst = (text) => (text ? text.charAt(0).toLowerCase() + text.slice(1) : text);

export function windowTitle(win) {
  const after = win.after_name;
  const before = win.before_name;
  if (after == null && before == null) return "Every movement";
  if (after == null) return `Before ${lowerFirst(before)}`;
  if (before == null) return `Since ${lowerFirst(after)}`;
  return `${after} → ${lowerFirst(before)}`;
}

export function windowDates(win) {
  if (!win.movements) return "no cash moved";
  const span =
    win.first_date === win.last_date
      ? day(win.first_date)
      : `${day(win.first_date)} – ${day(win.last_date)}`;
  return `${span} · ${win.movements} movement${win.movements === 1 ? "" : "s"}`;
}

// "1.16× the $230,531 paid on 28 Aug 2026", in the currency the server
// compared in. When the sales are krona and the payment dollars the ratio is
// dollars over dollars, the sales converted at each sale day's fixing.
export function coverText(funding) {
  if (funding.covered === null || funding.covered === undefined) {
    return "no cover figure — the sales and the funding are in different currencies and an exchange rate is missing";
  }
  const cost =
    funding.covered_in === "USD"
      ? Math.abs(Number(funding.cash.usd))
      : Math.abs(Number(funding.cash.by_currency?.[funding.covered_in] || 0));
  return `${Number(funding.covered).toFixed(2)}× the ${dealMoney(cost, funding.covered_in)} paid on ${day(funding.date)}`;
}

// Which fundings get a headline tile. Every one after the first, since those
// are the question; the first only when something had come back before it —
// Valuno sold collateral shares for a week before tranche 1's wire.
export function soldBeforeTiles(rounds) {
  return (rounds?.fundings || []).filter(
    (f, i) => i > 0 || amountsOf(f.proceeds_before).length > 0
  );
}

// What a tile is headed. It says what the figure under it is, and nothing
// else: shares sold when only shares were sold, notes repaid when only notes
// were, and "proceeds" with the kinds listed beneath when it is more than one.
// Brian, 28 September 2026, on DevvStream's redemptions: "yes thats cash in
// the door for sure".
export function proceedsHeading(funding, labels) {
  const kinds = Object.keys(funding.proceeds_before_by_kind || {});
  const before = lowerFirst(funding.name);
  if (!kinds.length) return `Nothing came back before ${before}`;
  if (kinds.length === 1) return `${labels[kinds[0]] || kinds[0]} before ${before}`;
  return `Proceeds before ${before}`;
}

// What a funding bought, for the chip on its row: "Shares", "Note",
// "Note + shares". Empty when the ledger does not say, rather than a guess.
export function boughtText(funding) {
  const bought = funding.bought || [];
  if (!bought.length) return "";
  const joined = bought.join(" + ");
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

const plainAmount = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(value || 0));

export function registerText(tranche) {
  const bits = [];
  if (tranche.amount !== null && tranche.amount !== undefined) {
    bits.push(
      tranche.currency
        ? `${dealMoney(tranche.amount, tranche.currency)} on the tranche register`
        : `${plainAmount(tranche.amount)} on the tranche register, no currency recorded`
    );
  } else {
    bits.push("on the tranche register with no amount");
  }
  const gap = Number(tranche.days_from_record || 0);
  const dated = `dated ${day(tranche.tranche_date)}`;
  if (gap === 0) bits.push(dated);
  else if (gap > 0) bits.push(`${dated}, ${gap} day${gap === 1 ? "" : "s"} before the cash left`);
  else bits.push(`${dated}, ${-gap} day${gap === -1 ? "" : "s"} after the cash left`);
  return bits.join(", ");
}

// The figures a window shows: every one the server lists that moved, in its
// order and its words, then the net.
export function windowFigures(win, buckets) {
  return [
    ...(buckets || [])
      .filter((b) => amountsOf(win[b.key]).length)
      .map((b) => ({ key: b.key, label: b.label })),
    { key: "net", label: "Net" },
  ];
}

export const smallCaps = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".6px",
  textTransform: "uppercase",
  color: theme.textMuted,
};

function RoundFigure({ label, figure, strong }) {
  const { main, sub, sign } = figureLines(figure);
  const color = sign < 0 ? theme.danger : sign > 0 ? theme.success : theme.textMuted;
  return (
    <div>
      <div style={{ ...smallCaps, fontSize: 10, marginBottom: 3 }}>{label}</div>
      <div className="dr-round-num" style={{ fontSize: 14, fontWeight: strong ? 600 : 500, color }}>
        {main}
      </div>
      {/* Wraps between parts, never inside one. */}
      {sub && <div style={{ fontSize: 11, color: theme.textMuted }}>{sub}</div>}
    </div>
  );
}

function SoldBeforeTile({ funding, labels }) {
  const usd = usdText(funding.proceeds_before);
  const sameDay = amountsOf(funding.same_day_proceeds);
  const kinds = Object.entries(funding.proceeds_before_by_kind || {});
  const otherIn = amountsOf(funding.other_in_before);
  return (
    <div
      style={{
        background: theme.kpiBg,
        border: `1px solid ${theme.kpiBorder}`,
        borderRadius: theme.radius,
        padding: "14px 16px",
      }}
    >
      <div style={smallCaps}>{proceedsHeading(funding, labels)}</div>
      <div className="dr-round-num" style={{ fontSize: 22, fontWeight: 600, color: theme.success, marginTop: 6 }}>
        {nativeText(funding.proceeds_before)}
      </div>
      {usd && (
        <div style={{ fontSize: 13, color: theme.textSecondary }}>
          <span className="dr-round-num">{usd}</span>
          {funding.proceeds_before.usd != null ? " at each day's fixing" : ""}
        </div>
      )}
      {funding.previous_name && amountsOf(funding.proceeds_since_previous).length > 0 &&
        nativeText(funding.proceeds_since_previous) !== nativeText(funding.proceeds_before) && (
          <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>
            <span className="dr-round-num">{figureLines(funding.proceeds_since_previous).main}</span>{" "}
            of it since {lowerFirst(funding.previous_name)}
          </div>
        )}
      {kinds.length > 1 && (
        <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 6 }}>
          {kinds.map(([key, figure]) => (
            <div key={key}>
              {labels[key] || key} <span className="dr-round-num">{figureLines(figure).main}</span>
            </div>
          ))}
        </div>
      )}
      {amountsOf(funding.proceeds_before).length > 0 && (
        <div style={{ fontSize: 12, color: theme.text, marginTop: 8 }}>{coverText(funding)}</div>
      )}
      {otherIn.length > 0 && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
          Plus {figureLines(funding.other_in_before).main} of other cash in — interest and
          fees — which is not in the figure above.
        </div>
      )}
      {sameDay.length > 0 && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
          Another {nativeText(funding.same_day_proceeds)} came back on {day(funding.date)} itself,
          and is counted after the funding.
        </div>
      )}
    </div>
  );
}

function RoundWindow({ win, buckets }) {
  return (
    <div className="dr-round-step">
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px", alignItems: "baseline", marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{windowTitle(win)}</span>
        <span style={{ fontSize: 12, color: theme.textMuted }}>{windowDates(win)}</span>
      </div>
      {win.movements > 0 && (
        <div className="dr-round-figures">
          {windowFigures(win, buckets).map((f) => (
            <RoundFigure key={f.key} label={f.label} figure={win[f.key]} strong={f.key === "net"} />
          ))}
        </div>
      )}
    </div>
  );
}

function RoundFunding({ funding }) {
  const usd = usdText(funding.cash);
  const bought = boughtText(funding);
  return (
    <div
      className="dr-round-step dr-round-funding"
      style={{
        background: theme.accentLight,
        border: `1px solid ${theme.accentBorder}`,
        borderRadius: theme.radius,
      }}
    >
      <span className="dr-round-dot" style={{ background: theme.accent, borderColor: theme.bgCard }} />
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "4px 16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>
            {funding.name} funded · {day(funding.date)}
          </span>
          {bought && (
            // Blue on an equity tranche, so shares on a note deal read as
            // something other than another note at a glance. `ui.js`'s Badge
            // knows green, red, yellow, blue, amber and gray; any other word
            // falls back to gray without a warning.
            <Badge text={bought} variant={funding.short?.startsWith("E") ? "blue" : "gray"} />
          )}
        </div>
        <div className="dr-round-num" style={{ fontSize: 14, color: theme.danger }}>
          {nativeText(funding.cash)}
          {usd ? ` (${usd})` : ""}
        </div>
      </div>
      {/* The register's own label, unless it only repeats the heading. */}
      {(funding.tranche.label || "").trim() &&
        funding.tranche.label.trim().toLowerCase() !== (funding.name || "").toLowerCase() && (
          <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>{readerText(funding.tranche.label)}</div>
        )}
      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{registerText(funding.tranche)}</div>
    </div>
  );
}

export function FundingRounds({ deal }) {
  const rounds = deal.funding_rounds;
  const tiles = soldBeforeTiles(rounds);
  const labels = Object.fromEntries((rounds.buckets || []).map((b) => [b.key, b.label]));
  const found = rounds.fundings.length;
  const steps = [];
  rounds.windows.forEach((win, i) => {
    steps.push(<RoundWindow key={`w${i}`} win={win} buckets={rounds.buckets} />);
    if (rounds.fundings[i]) {
      steps.push(<RoundFunding key={`f${i}`} funding={rounds.fundings[i]} />);
    }
  });

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Funding rounds
          <InfoTip>
            <p>{rounds.reads_as}</p>
            <p>
              {rounds.converted
                ? "Dollars are at each movement's own fixing, the rates the cash curve uses."
                : "No dollar figures: an exchange rate one of this deal's dates needs is missing."}
            </p>
          </InfoTip>
        </span>
      }
      subtitle="What came back between tranches — shares sold, notes repaid — split on the day each tranche's cash left"
      action={
        found === rounds.tranches_recorded ? (
          <Badge text={`${found} tranches`} variant="gray" />
        ) : (
          <Badge text={`${found} of ${rounds.tranches_recorded} tranches on the ledger`} variant="yellow" dot />
        )
      }
    >
      {tiles.length > 0 && (
        <div className="dr-rounds-tiles">
          {tiles.map((f) => (
            <SoldBeforeTile key={f.number} funding={f} labels={labels} />
          ))}
        </div>
      )}

      <div className="dr-rounds-rail" style={{ borderLeftColor: theme.border }}>
        {steps}
      </div>

      {rounds.unmatched_tranches.length > 0 && (
        <div className="dr-warning" style={{ marginTop: theme.space.lg, fontSize: 12 }}>
          <strong style={{ color: theme.text }}>
            {rounds.unmatched_tranches.length === 1 ? "A tranche is" : "Tranches are"} on the
            register but not split here.
          </strong>
          <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
            {rounds.unmatched_tranches.map((u) => (
              <li key={u.id} style={{ marginBottom: 2 }}>
                {u.name || `Tranche ${u.number}`}
                {u.label && u.label !== u.name ? ` (${readerText(u.label)})` : ""}: {readerText(u.why)}.
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/**
 * What the Tranches tab says when there is nothing to split. "Single tranche"
 * only when the register says one; an empty register is not evidence of one
 * funding, so it is said as what it is.
 */
export function singleTrancheText(deal) {
  const recorded = deal?.funding_rounds?.tranches_recorded || 0;
  const first = deal?.summary?.first_date;
  if (recorded === 1) {
    return `This deal was funded in a single tranche, so there is nothing to split${
      first ? `; its first cash movement was on ${day(first)}` : ""
    }. The whole of its cash is on the Cash curve tab.`;
  }
  return `No tranches are recorded for this deal, so it is read as one funding${
    first ? ` from its first cash movement on ${day(first)}` : ""
  }. Tranches are added on the Deals page; the whole of its cash is on the Cash curve tab.`;
}
